import { useState } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import { nextStep, ONBOARDING_BASE_PATH } from "../registry";
import { useFlowState, useFlowActions } from "../../state/FlowProvider";
import AgeGateDialog from "../../components/AgeGateDialog";
import { meetsMinimumAge, toIsoDate, fromIsoDate } from "../../age";
import { onboardingTokens } from "../../theme";
import { t } from "../../../../locales/t";
import FormSubmitButton from "../../components/FormSubmitButton";

/**
 * Date of birth, and the 16+ gate behind it.
 *
 * Three separate fields rather than a date picker: a picker is a poor way to
 * enter a year forty years in the past, needing dozens of taps or a keyboard
 * interaction most people never discover. Three numeric fields are typed
 * straight through and work with a keyboard alone.
 *
 * An under-age date is never written to flow state. That is not cosmetic: the
 * route guard admits `create_account` on `Boolean(dob)`, so storing a rejected
 * date would let a blocked visitor reach account creation by editing the URL —
 * the gate would be a dialog rather than a gate.
 */
const DobStep = ({ step }) => {
  const state = useFlowState();
  const { setDob } = useFlowActions();
  const navigate = useNavigate();

  // Seeded from the flow so returning to this step shows what was entered.
  const [fields, setFields] = useState(() => fromIsoDate(state.dob));
  const [submitted, setSubmitted] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  const iso = toIsoDate(fields);
  const complete = Object.values(fields).every((v) => String(v).trim() !== "");
  const showInvalid = submitted && complete && !iso;

  const update = (key) => (event) => {
    // Digits only. Stripping here rather than validating later means a pasted
    // "05/1990" cannot silently become an unparseable field.
    const value = event.target.value.replace(/\D/g, "");
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleContinue = () => {
    setSubmitted(true);
    if (!iso) return;

    if (!meetsMinimumAge(iso)) {
      setGateOpen(true);
      return;
    }

    setDob(iso);

    // Routed against the state this answer produces: dispatch has not landed
    // yet, so the registry would otherwise be asked about a flow with no date.
    const next = nextStep(step.id, { ...state, dob: iso });
    if (next) navigate(`${ONBOARDING_BASE_PATH}/${next.path}`);
  };

  const field = (key, max) => (
    <TextField
      label={t(`onboarding.dob.${key}`)}
      placeholder={t(`onboarding.dob.${key}Placeholder`)}
      value={fields[key]}
      onChange={update(key)}
      error={showInvalid}
      inputMode="numeric"
      slotProps={{ htmlInput: { maxLength: max, pattern: "[0-9]*" } }}
      fullWidth
    />
  );

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        handleContinue();
      }}
    >
      <Typography variant="h1" sx={{ mb: 1.5 }}>
        {t("onboarding.dob.heading")}
      </Typography>

      <Typography
        sx={{
          color: onboardingTokens.mutedForeground,
          lineHeight: 1.65,
          mb: 4,
        }}
      >
        {t("onboarding.dob.body")}
      </Typography>

      <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
        <Typography component="legend" sx={{ mb: 1.5, fontWeight: 600 }}>
          {t("onboarding.dob.legend")}
        </Typography>

        <Stack direction="row" gap={2}>
          {field("day", 2)}
          {field("month", 2)}
          {field("year", 4)}
        </Stack>
      </Box>

      {showInvalid && (
        <Typography
          role="alert"
          sx={{
            mt: 1.5,
            fontSize: "0.875rem",
            color: onboardingTokens.destructive,
          }}
        >
          {t("onboarding.dob.invalid")}
        </Typography>
      )}

      <Stack
        direction="row"
        gap={1.25}
        sx={{
          mt: 3,
          px: 2,
          py: 1.5,
          borderRadius: "0.75rem",
          backgroundColor: onboardingTokens.lightTeal,
          border: `1px solid ${onboardingTokens.border}`,
        }}
      >
        <InfoOutlinedIcon
          aria-hidden="true"
          sx={{
            fontSize: 16,
            mt: "1px",
            flexShrink: 0,
            color: onboardingTokens.mutedForeground,
          }}
        />
        <Typography
          sx={{
            fontSize: "0.8125rem",
            lineHeight: 1.55,
            color: onboardingTokens.mutedForeground,
          }}
        >
          {t("onboarding.dob.privacy")}
        </Typography>
      </Stack>

      <FormSubmitButton
        variant="contained"
        fullWidth
        disabledReason={!complete ? t("onboarding.dob.incomplete") : ""}
        sx={{ mt: 4, py: 1.5 }}
      >
        {t("onboarding.dob.continue")}
      </FormSubmitButton>

      <AgeGateDialog open={gateOpen} onClose={() => setGateOpen(false)} />
    </Box>
  );
};

export default DobStep;
